import { isInsideOnboardingAtom, isOnboardingAtom, registrationMethodAtom } from 'app/atoms';
import { CommonActions } from '@react-navigation/native';
import { presentNotificationModalAtom } from 'app/atoms/ui';
import { useForbidBackAction } from 'app/hooks/use-forbid-back-action';
import { trackOnFacebook } from 'app/lib/analytics';
import { recordError } from 'app/lib/monitoring';
import { hasNotificationPermission } from 'app/lib/notifications';
import { restoreGuestReturnLocation } from 'app/lib/guest-return-location';
import type * as onboarding from 'app/screens/onboarding/navigator';
import { WhiteLabel } from 'app/types/white-labels';
import { FeatureFlag, isFeatureEnabled, isWhiteLabel } from 'app/utils/helpers';
import { getIsLargeUI, isMobileNative } from 'app/utils/web-helpers';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { AppEventsLogger } from 'react-native-fbsdk-next';
import { useNavigation, useNavigationOptions, useRoute } from './navigation';

type Step = {
  [Key in keyof onboarding.Parameters]: {
    name: Key;
    mode?: 'push' | 'reset';
    params?: onboarding.Parameters[Key];
  };
}[keyof onboarding.Parameters];

const shouldPromptSharerType = isFeatureEnabled(FeatureFlag.promptSharerType) && !isWhiteLabel(WhiteLabel.Mutuo);

const steps: Step[] = [
  { name: 'contact-information', params: undefined as onboarding.Parameters['contact-information'] },
  { name: 'credentials', params: undefined as onboarding.Parameters['credentials'] },
  { name: 'personal-information', params: { hideCross: true } },
  ...(shouldPromptSharerType ? ([{ name: 'choose-sharer-type', params: { hideCross: true } }] as Step[]) : []),
  { name: 'guidelines', params: { hideCross: true } },
];

export function useOnboarding() {
  const route = useRoute();
  const navigation = useNavigation<'onboarding'>();
  const setIsInsideOnboarding = useSetAtom(isInsideOnboardingAtom);

  useEffect(() => {
    setIsInsideOnboarding(true);
  }, [setIsInsideOnboarding]);

  let currentIndex = -1;
  for (let i = 0; i < steps.length; i++) {
    const entry = steps[i];
    if (entry?.name === route.name) {
      currentIndex = i;
      break;
    }
  }
  const setModalVisible = useSetAtom(presentNotificationModalAtom);
  const setIsOnboarding = useSetAtom(isOnboardingAtom);
  const registrationMethod = useAtomValue(registrationMethodAtom);
  useForbidBackAction(route.params && 'hideCross' in route.params && route.params.hideCross);

  useNavigationOptions<'onboarding'>(navigation, {
    headerBackVisible: false,
  });

  const isOnboarding = useAtomValue(isOnboardingAtom);

  const goToNext = () => {
    if (!isOnboarding) return;
    const nextRoute = steps[currentIndex + 1];

    if (!nextRoute) {
      setIsOnboarding(false);
      setIsInsideOnboarding(false);
      if (isMobileNative) {
        trackOnFacebook(AppEventsLogger.AppEvents.CompletedRegistration, {
          [AppEventsLogger.AppEventParams.RegistrationMethod]: registrationMethod,
        });
      }

      if (!restoreGuestReturnLocation()) {
        navigation.reset({ routes: [{ name: 'main' }] });
      }

      // NotificationWarnModal is not shown on largeUI web — skip setting the blocking atom.
      if (!getIsLargeUI()) {
        hasNotificationPermission()
          .then((hasPermission) => {
            if (!hasPermission) {
              setModalVisible(true);
            }
          })
          .catch((err: unknown) => {
            recordError(err);
          });
      }
    } else {
      navigation.dispatch(CommonActions.navigate(nextRoute.name, nextRoute.params, { pop: true }));
    }
  };

  return { current: currentIndex, goToNext, max: steps.length };
}
